import { CoreButton, CoreInput, CoreSidebarRight } from '@components'
import CoreLoader from '@components/CoreLoader'
import ExpenseTabs from '@components/ExpenseTabs'
import SearchIcon from '@images/icons/search-icon.svg?react'
import FiltersIcon from '@images/icons/transactions-filter-icon.svg?react'
import { toastFailure } from '@utils'
import { useAttentionNeededReportCount } from '@views/expenseReports/ExpenseReports/useAttentionNeededReportCount'
import { useCreateReportMutation, useGetReportListWithoutCacheMutation, useUserProfileQry } from 'mmfintech-backend-api'
import { fixDateOnly, isValidArray, tr, useDebounce, useFilter, useMatchMedia, usePagination } from 'mmfintech-commons'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import {
  type ExpenseReportsTab,
  getExpenseReportsFilterCount,
  getExpenseReportsTabStates,
  getExpenseReportsTabs
} from './ExpenseReports.helper'
import ReportsList from './ReportsList'
import ReportsSideFilter from './ReportsSideFilter'

import './expenseReports.scss'

const ExpenseReports = () => {
  const location = useLocation()
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search])
  const selectedTab = searchParams.get('tab')

  const [openFilterModal, setOpenFilterModal] = useState<boolean>(false)
  const [applyFilterFlag, setApplyFilterFlag] = useState<boolean>(false)

  const history = useHistory()

  const isLarge = useMatchMedia({ breakpoint: 1380, prefix: 'min' })
  const isCollapsedButtons = useMatchMedia({ breakpoint: 1600, prefix: 'max' })
  const outsideSearch = useMatchMedia({ breakpoint: 1480, prefix: 'min' })

  const { profile, profileLoading } = useUserProfileQry()

  const [getReportsWithoutCache, { data, isLoading }] = useGetReportListWithoutCacheMutation()

  const [_, { isSuccess: isCreateReportSuccess, reset: createReset }] = useCreateReportMutation({
    fixedCacheKey: 'refetch-reports'
  })

  const filterValues = useFilter({}, ['search'], ['fromDate', 'toDate'])
  const filterCount = useMemo(() => getExpenseReportsFilterCount(filterValues), [filterValues])

  const applyFilter = useCallback(() => setApplyFilterFlag(true), [])
  const applySearch = useDebounce(applyFilter, 300)

  const tabs = useMemo(() => getExpenseReportsTabs(profile?.userPermissions, profile?.customerRole), [profile])

  const { count: attentionNeededReportsCount } = useAttentionNeededReportCount()

  const clearAll = useCallback(() => {
    filterValues.reset(['counterpartyId', 'foreignId', 'states'])
    setApplyFilterFlag(true)
  }, [filterValues])

  const handleTabChanged = useCallback(
    (value: string) => {
      if (selectedTab !== value && tabs?.some(tab => tab.name === value)) {
        history.push(`${history.location.pathname}?tab=${value}`)
      }
    },
    [tabs, selectedTab, history]
  )

  // biome-ignore lint/correctness/useExhaustiveDependencies: select the first tab when no tab or invalid tab is selected
  useEffect(() => {
    if (isValidArray(tabs)) {
      if (!selectedTab || !tabs.some(tab => tab.name === selectedTab)) {
        handleTabChanged(tabs[0].name)
      }
    }
  }, [tabs, selectedTab])

  useEffect(() => {
    if (isCreateReportSuccess) {
      createReset()
      setApplyFilterFlag(true)
    }
  }, [isCreateReportSuccess, createReset])

  // biome-ignore lint/correctness/useExhaustiveDependencies: reload list on search field changed
  useEffect(() => {
    if (outsideSearch) {
      applySearch()
    }
  }, [outsideSearch, filterValues.get('search')])

  // biome-ignore lint/correctness/useExhaustiveDependencies: apply filter only when applyFilter flag changed to true
  useEffect(() => {
    if (applyFilterFlag) {
      setApplyFilterFlag(false)
      pagination.reload()
    }
  }, [applyFilterFlag])

  const prepareRequest = useCallback(
    (params: any, tab: ExpenseReportsTab) => {
      const states = getExpenseReportsTabStates(tab)
      return {
        ...params,
        ...filterValues.prepare(),
        ...(filterValues.get('from') ? { fromDate: fixDateOnly(filterValues.get('from')) } : {}),
        ...(filterValues.get('to') ? { toDate: fixDateOnly(filterValues.get('to')) } : {}),
        ...(isValidArray(states) ? { states } : {}),
        deleted: false,
        hideDraftReports: tab === 'all-awaiting-approval' || tab === 'your-action-needed',
        ...(tab === 'your-action-needed' ? { requiredAction: true } : {}),
        ...(tab === 'your-reports' || tab === 'draft' ? { owner: true } : {})
      }
    },
    [filterValues]
  )

  const reloadCallable = useCallback(
    async (params: any, onSuccess: (response: any) => void) => {
      if (selectedTab) {
        try {
          const response = await getReportsWithoutCache(
            prepareRequest(params, selectedTab as ExpenseReportsTab)
          ).unwrap()
          onSuccess(response)
        } catch (error) {
          toastFailure(error?.cause?.[0]?.cause || error.error)
        }
      }
    },
    [selectedTab, prepareRequest, getReportsWithoutCache]
  )

  const pagination = usePagination({
    rowsPerPage: 7,
    reload: reloadCallable
  })

  // biome-ignore lint/correctness/useExhaustiveDependencies: reset and reload only when tab changed
  useEffect(() => {
    if (selectedTab) {
      filterValues.reset(['counterpartyId', 'foreignId', 'states'])
      pagination.reload({ page: 0 })
    }
  }, [selectedTab])

  return profileLoading ? (
    <CoreLoader />
  ) : (
    <div className='reports-wrapper'>
      <div className='table-wrapper'>
        <div className='reports-header'>
          {
            <ExpenseTabs
              config={tabs}
              counts={{ 'your-action-needed': attentionNeededReportsCount }}
              currentTab={selectedTab}
              setTab={handleTabChanged}
            />
          }

          {selectedTab && (
            <div className='reports-header-filters'>
              <CoreInput
                type='text'
                name='search'
                {...filterValues.registerInput('search')}
                label={tr('FRONTEND.EXPENSES.FILTER.SEARCH', 'Search')}
                LeftIcon={<SearchIcon />}
                data-test='filter-search'
              />
              <CoreButton
                onClick={() => setOpenFilterModal(true)}
                variation='tertiary'
                size='large'
                className='transaction-filter-button'
                text={tr('FRONTEND.TRANSACTIONS.FILTER.FILTERS_BUTTON', 'Filters')}
                LeftIcon={<FiltersIcon />}
                CollapsedIcon={<FiltersIcon />}
                tooltip={isCollapsedButtons ? tr('FRONTEND.TRANSACTIONS.FILTER.FILTERS_BUTTON', 'Filters') : ''}
                collapsed={isCollapsedButtons && isLarge}
                RightIcon={filterCount > 0 && <span className='transaction-filter-count-icon'>{filterCount}</span>}
              />
            </div>
          )}
        </div>

        {selectedTab &&
          (isLoading ? (
            <CoreLoader />
          ) : (
            <ReportsList
              data={data?.content || []}
              pagination={pagination}
              filterValues={filterValues}
              states={getExpenseReportsTabStates(selectedTab)}
            />
          ))}
      </div>

      <CoreSidebarRight
        content={
          <ReportsSideFilter
            filters={filterValues}
            filterCount={filterCount}
            applyFilter={applyFilter}
            modalHide={() => setOpenFilterModal(false)}
            clearAll={clearAll}
          />
        }
        visible={openFilterModal}
        options={{ size: 'extra-large' }}
        header={tr('FRONTEND.TRANSACTIONS.FILTER.TITLE', 'Filters')}
        onClose={() => setOpenFilterModal(false)}
      />
    </div>
  )
}

export default ExpenseReports
